
import React, { useState, useEffect } from 'react';
import { cn } from "@/lib/utils";
import { getOptimizedImageUrl, createSrcSet, getOptimalImageWidth } from '@/utils/imageOptimizer';

interface ImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  fill?: boolean;
  fallbackSrc?: string;
  sizes?: string;
  priority?: boolean;
  onLoadingComplete?: () => void;
  quality?: number;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
}

const Image = React.forwardRef<HTMLImageElement, ImageProps>(
  ({ 
    className, 
    fill = false, 
    alt = "", 
    fallbackSrc = "/placeholder.svg", 
    onError, 
    sizes = "100vw",
    priority = false,
    onLoadingComplete,
    quality = 85,
    style,
    objectFit = 'cover',
    ...props 
  }, ref) => {
    const [error, setError] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [loadStartTime] = useState<number>(performance.now());
    const imgRef = React.useRef<HTMLImageElement | null>(null);
    
    // Get optimized image URL from source
    const imageUrl = props.src ? props.src.toString() : '';
    
    // Generate responsive srcset for the image
    const srcSet = imageUrl ? createSrcSet(imageUrl.toString(), [400, 800, 1200, 1600], quality) : undefined;
    
    // Preload high priority images
    useEffect(() => {
      if (priority && imageUrl && typeof window !== 'undefined') {
        // Create an actual Image object for better browser support
        const preloadImg = new window.Image();
        preloadImg.src = imageUrl;
        
        // Also add a preload link for browsers that support it
        const preloadLink = document.createElement('link');
        preloadLink.rel = 'preload';
        preloadLink.as = 'image';
        preloadLink.href = imageUrl;
        if (sizes) {
          preloadLink.setAttribute('sizes', sizes);
        }
        document.head.appendChild(preloadLink);
        
        return () => {
          try {
            document.head.removeChild(preloadLink);
          } catch (e) {
            // Ignore if already removed
          }
        };
      }
    }, [priority, imageUrl, sizes]);
    
    const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
      console.error("Image error for:", props.src);
      setError(true);
      if (onError) {
        onError(e);
      }
    };
    
    const handleLoad = () => {
      setLoaded(true);
      
      // Log performance metrics
      const loadTimeMs = performance.now() - loadStartTime;
      if (process.env.NODE_ENV === 'development') {
        console.log(`Image loaded in ${loadTimeMs.toFixed(2)}ms:`, imageUrl);
      }
      
      if (onLoadingComplete) {
        onLoadingComplete();
      }
    };
    
    // Calculate width/height based on fill property
    const imgSize = !fill && props.width && props.height
      ? { width: props.width, height: props.height }
      : undefined;
    
    // Combined styles including objectFit
    const combinedStyles = {
      objectFit,
      objectPosition: 'center',
      ...(style || {})
    };
    
    return (
      <div className={cn(
        "relative",
        fill ? "w-full h-full" : "",
        className
      )}
        style={imgSize ? { width: imgSize.width, height: imgSize.height } : undefined}
        data-testid="image-container"
      >
        {!loaded && !error && (
          <div 
            className="bg-gray-100 animate-pulse h-full w-full"
            style={fill ? { position: 'absolute', inset: 0 } : {}}
            aria-hidden="true"
          />
        )}
        
        <img
          ref={(node) => {
            imgRef.current = node;
            if (typeof ref === 'function') {
              ref(node);
            } else if (ref) {
              ref.current = node;
            }
          }}
          className={cn(
            "transition-opacity duration-300",
            fill ? "absolute inset-0 h-full w-full" : "max-w-full h-auto",
            error ? "hidden" : loaded ? "opacity-100" : "opacity-0"
          )}
          src={error ? fallbackSrc : imageUrl}
          alt={alt}
          onError={handleError}
          onLoad={handleLoad}
          loading={priority ? "eager" : "lazy"}
          decoding={priority ? "sync" : "async"}
          sizes={sizes}
          width={props.width}
          height={props.height}
          srcSet={!error ? srcSet : undefined}
          fetchPriority={priority ? "high" : "auto"}
          style={combinedStyles}
          {...props}
        />
        
        {error && (
          <div className="w-full h-full flex items-center justify-center bg-gray-100 text-center p-4">
            <img 
              src={fallbackSrc}
              alt={alt || "Image failed to load"}
              className="max-w-full max-h-full object-contain"
              onError={() => console.error("Even fallback image failed to load")}
            />
          </div>
        )}
      </div>
    );
  }
);

Image.displayName = "Image";

export default Image;
